import { type BadgeColor, IconButton, StatusBadge, type StatusBadgeTooltip, Tooltip } from '@civilgrid/ui/components';
import { Link, useParams } from '@tanstack/react-router';
import type { CellContext, SortingFn } from '@tanstack/react-table';
import { isEmpty, isUndefined } from 'lodash';
import { type ReactNode, useState } from 'react';
import { useSnapshot } from 'valtio';
import {
  downloadCSAttachments as apiDownloadCSAttachments,
  updateUtilityNote as apiUpdateUtilityNote,
} from '~/api/projects2';
import { getReasonForError } from '~/common/getReasonForError';
import { notifyDownloadError } from '~/common/notifyDownloadError';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { rowBadgeLabel } from '~/common/rowBadgeLabel';
import { Outcome, RequestStatus, RequestType, UtilityPresence } from '~/common/types';
import InfoTooltip from '~/components2/InfoTooltip';
import { SendEmailModal } from '~/components2/SendEmailModal';
import { AUTO_COL_WIDTH, type ColumnDef } from '~/components2/Table';
import FreeFormTextCell from '~/components2/Table/Cells/FreeFormTextCell';
import type { ProjectDetailsParams } from '../../..';
import {
  compareAgencyRows,
  hasNoAssetsPresent,
  isConnectbaseCovered,
  LIVE_DATA_AVAILABLE_TOOLTIP,
  type UtilityAgency,
} from '../../common';
import EstTimeCell from '../../common/EstTimeCell';
import { makeEstTimeSortHeader } from '../../common/EstTimeSortHeader';
import {
  type EstTimeRequest,
  type EstTimeState,
  getEstTimeState,
  getResponseConfidenceRank,
  surfacedEstimateDays,
} from '../../common/estimatedResponseTime';
import { blocksNewRequest } from '../../common/requestOnlyAgencies';
import store from '../../store';
import { isSubGeometryParentRow, requestMatchesRow } from '../grouping';
import StartDownload from '../StartDownload';
import AgencyNameCell from './AgencyNameCell';
import DocumentsCell from './DocumentsCell';
import LatestUpdateCell from './LatestUpdateCell';

type Cell = CellContext<UtilityAgency, any>;

const rowName = (a: UtilityAgency) => `${a.agencyName} ${a.type}`;

const renderAvailabilityDigital = (cell: Cell) => {
  const a = cell.row.original;
  // On a summary parent, agency-level columns (Asset/Live Map/Download) stay visible while collapsed
  // and blank once expanded (the Region children carry them then).
  if (isSubGeometryParentRow(a) && cell.row.getIsExpanded()) return null;
  const noAssetsPresent = hasNoAssetsPresent(a);

  const label = (status: string) => rowBadgeLabel('Live Map', status, rowName(a));

  if (a.isDigitized) {
    return (
      <StatusBadge
        color="green"
        aria-label={label('Yes - Digitized')}
        data-testid="live-map-cell"
        tooltip={{
          title: 'Digitized Assets',
          description: 'Assets have been digitized for this project by CivilGrid.',
        }}
      >
        Yes - Digitized
      </StatusBadge>
    );
  }

  if (noAssetsPresent) {
    return (
      <StatusBadge
        aria-label={label('No Assets Present')}
        data-testid="live-map-cell"
        tooltip={{
          title: 'No Assets Present',
          description:
            'Based on the digital data in our platform, there are no assets present in the project boundary.',
        }}
        color="green"
      >
        No Assets Present
      </StatusBadge>
    );
  }

  if (a.availableDigitally) {
    return (
      <StatusBadge
        aria-label={label('Yes')}
        data-testid="live-map-cell"
        tooltip={{
          title: 'Assets Present',
          description: 'Digital data with layer attributes is available on the map.',
        }}
        color="green"
      >
        Yes
      </StatusBadge>
    );
  }

  if (isConnectbaseCovered(a, store.connectbaseCoverageEnabled)) {
    return (
      <StatusBadge
        aria-label={label('Yes')}
        data-testid="live-map-connectbase-cell"
        tooltip={LIVE_DATA_AVAILABLE_TOOLTIP}
        color="green"
      >
        Yes
      </StatusBadge>
    );
  }

  return (
    <StatusBadge
      aria-label={label('No')}
      data-testid="live-map-cell"
      tooltip={{
        title: 'Data Not Available',
        description: 'Digital data NOT available on the map currently. CivilGrid can request maps on your behalf.',
      }}
      color="grey"
    >
      No
    </StatusBadge>
  );
};

const getRequest = (agencyId: string, type: RequestType, subGeometryId?: string | null) => {
  return Object.values(store.dataRequests).find(
    (r) => r.type === type && requestMatchesRow(r, { agencyId, agencySubGeometryId: subGeometryId }),
  );
};

const OpenDrawerLink = ({
  drawer,
  children,
  onClick,
  'data-testid': dataTestId,
}: {
  drawer: string;
  children: ReactNode;
  onClick?: () => void;
  'data-testid'?: string;
}) => {
  return (
    <Link
      to="/projects/$projectId/$tabId"
      params={{ projectId: store.project?.id ?? '', tabId: 'utilities' }}
      search={{ drawer }}
      className="text-blue-50 underline hover:text-blue-60"
      onClick={onClick}
      data-testid={dataTestId}
    >
      {children}
    </Link>
  );
};

export const renderRequestStatus = (
  requestStatus: RequestStatus,
  utilityPresence?: UtilityPresence,
  outcome?: Outcome,
) => {
  let color: BadgeColor = 'grey';
  let tooltip: StatusBadgeTooltip = {
    title: 'Contact Support',
    description: 'There is an issue with this request, please contact Support.',
  };
  let trigger = 'Contact Support';
  if (requestStatus === RequestStatus.Pending) {
    color = 'blue';
    tooltip = {
      title: 'Pending',
      description: 'Your request has been received. We will review it shortly.',
    };
    trigger = 'Pending';
  } else if (
    requestStatus === RequestStatus.InProgress0 ||
    requestStatus === RequestStatus.InProgress1 ||
    requestStatus === RequestStatus.InProgress2 ||
    requestStatus === RequestStatus.InProgress3
  ) {
    color = 'blue';
    tooltip = {
      title: 'In Progress',
      description: 'Your request is currently in progress.',
    };
    trigger = 'In Progress';
  } else if (requestStatus === RequestStatus.ActionRequired) {
    color = 'yellow';
    tooltip = {
      title: 'Action Required',
      description: (
        <span>
          Please see recent <OpenDrawerLink drawer="project-channel">chat messages</OpenDrawerLink>.
        </span>
      ),
    };
    trigger = 'Action Required';
  } else if (requestStatus === RequestStatus.OnHoldPerAgency) {
    color = 'blue';
    tooltip = {
      title: 'On Hold (Per Agency)',
      description: <span>Agency has indicated they require additional time to complete the request.</span>,
    };
    trigger = 'On Hold (Per Agency)';
  } else if (requestStatus === RequestStatus.Cancelled) {
    color = 'grey';
    tooltip = {
      title: 'Cancelled',
      description: (
        <span>
          <OpenDrawerLink drawer="project-channel">Contact us</OpenDrawerLink> if you have any questions.
        </span>
      ),
    };
    trigger = 'Cancelled';
  } else if (requestStatus === RequestStatus.Dismissed) {
    color = 'grey';
    tooltip = {
      title: 'Dismissed',
      description: (
        <span>
          <OpenDrawerLink drawer="project-channel">Contact us</OpenDrawerLink> if you have any questions.
        </span>
      ),
    };
    trigger = 'Dismissed'; // 👈 this case should never occur in theory
  } else if (requestStatus === RequestStatus.Complete) {
    if (utilityPresence === UtilityPresence.Present) {
      if (outcome === Outcome.AgencyNotSharing) {
        color = 'yellow';
        trigger = 'Complete - Present, Agency will not share documents';
        tooltip = {
          title: 'Request Complete - Assets Present',
          description: 'Agency has confirmed that utilities are present but will not share documents.',
        };
      } else if (outcome === Outcome.DocumentsReceived) {
        color = 'green';
        trigger = 'Complete - Assets Present, Documents Received';
        tooltip = {
          title: 'Request Complete - Utilities Present',
          description: 'Agency has confirmed that utilities are present and documents have been received.',
        };
      }
    } else if (utilityPresence === UtilityPresence.NoAssetsPresent) {
      if (outcome === Outcome.AgencyResponded) {
        color = 'green';
        trigger = 'Complete - NAP';
        tooltip = {
          title: 'Request Complete - No Assets Present',
          description: 'Agency has confirmed that no assets are present in project area',
        };
      } else if (outcome === Outcome.ReferredToOtherAgency) {
        color = 'green';
        trigger = 'Complete - See Note';
        tooltip = {
          title: 'Referred to another agency',
          description: 'Agency told us to contact another agency. We initiated another request under a new agency.',
        };
      }
    } else if (utilityPresence === UtilityPresence.PresenceUnknown) {
      if (outcome === Outcome.AgencyUnresponsive) {
        color = 'yellow';
        trigger = 'Complete - See Note';
        tooltip = {
          title: 'Agency Unresponsive',
          description: 'Agency did not respond to our requests. We reached out 4 times.',
        };
      } else if (outcome === Outcome.AgencyNotSharing) {
        color = 'yellow';
        trigger = 'Complete - See Note';
        tooltip = {
          title: 'Agency Will Not Share',
          description: 'Agency policy not to share data.',
        };
      } else if (outcome === Outcome.ReferredToOtherAgency) {
        color = 'yellow';
        trigger = 'Complete - See Note';
        tooltip = {
          title: 'Referred to another agency',
          description: 'Agency told us to contact another agency. We initiated another request under a new agency.',
        };
      }
    } else if (utilityPresence === UtilityPresence.OutsideServiceArea) {
      color = 'green';
      trigger = 'Complete - See Note';
      tooltip = {
        title: 'Outside Service Area',
        description: 'Agency confirmed project boundary is outside their service area.',
      };
    }
  }

  return { color, tooltip, trigger };
};

const renderAvailabilityRecordsResearch = (cell: Cell) => {
  // when making changes here, also update Records Research Report job
  const cellType = cell.column.id as RequestType;
  const a = cell.row.original;
  if (isSubGeometryParentRow(a)) return null;
  const noAssetsPresent = hasNoAssetsPresent(a);
  const request = getRequest(a.agencyId, cellType, a.agencySubGeometryId);

  let testId: string | undefined;
  // 👇 these defaults should never be visible to the user
  let color: BadgeColor = 'grey';
  let tooltip: StatusBadgeTooltip = {
    title: 'Contact Support',
    description: 'There is an issue with this request, please contact Support.',
  };
  let trigger = 'Contact Support';

  if (request) {
    // has already been requested, display progress status
    ({ color, tooltip, trigger } = renderRequestStatus(
      request.requestStatus,
      request.utilityPresence,
      request.outcome,
    ));
  } else if (blocksNewRequest(a, request)) {
    trigger = '-';
    color = 'grey';
    tooltip = {
      title: 'Not Available',
      description: 'This agency is not in the project agency list, so a new request cannot be started here.',
    };
  } else {
    // not requested yet, display requestable or not
    if (cellType === RequestType.MapsRequested) {
      if (a.isPublished) {
        if (a.type === 'Fiber') {
          trigger = 'Request';
          tooltip = {
            title: 'Request Maps',
            description: (
              <span>
                Our fiber data generally includes only the trunk lines running in the public right-of-way. If you need
                more detail, you can request fiber detail maps from the agencies. Generally, cable and copper lines are
                not digitized.
              </span>
            ),
          };
          color = 'yellow';
        } else {
          if (noAssetsPresent) {
            color = 'green';
            trigger = 'N/A';
            tooltip = {
              title: 'Not Applicable',
              description:
                'Based on the digital data in our platform, there are no assets present in the project boundary.',
            };
          } else {
            color = 'grey';
            tooltip = {
              title: 'Not Applicable',
              description: 'Digital data available.',
            };
            trigger = 'N/A';
          }
        }
      } else if (isConnectbaseCovered(a, store.connectbaseCoverageEnabled)) {
        // A stored Connectbase route reads like published digital data: no maps request is offered.
        trigger = 'N/A';
        color = 'grey';
        testId = 'map-request-connectbase-cell';
        tooltip = { title: 'Not Applicable', description: 'Digital data available.' };
      } else {
        trigger = `Request`;
        color = 'yellow';
        tooltip = {
          title: 'Request Maps',
          description: (
            <span>
              Maps are available for request. Click <OpenDrawerLink drawer="request-data">here</OpenDrawerLink> to
              start.
            </span>
          ),
        };
      }
    } else if (cellType === RequestType.AsbuiltsRequested) {
      if (noAssetsPresent) {
        trigger = 'N/A';
        color = 'green';
        tooltip = {
          title: 'Not Applicable',
          description:
            'Based on the digital data in our platform, there are no assets present in the project boundary.',
        };
      } else if (a.asbuiltsAvailable) {
        trigger = 'Request';
        color = 'yellow';
        tooltip = {
          title: 'Request As-builts',
          description: (
            <span>
              As-builts are available for request. Click <OpenDrawerLink drawer="request-data">here</OpenDrawerLink> to
              start.
            </span>
          ),
        };
      } else {
        trigger = '-';
        tooltip = {
          title: 'Not Available',
          description: 'As-builts not available for this agency.',
        };
        color = 'grey';
      }
    }
  }

  const isAsbuilts = cellType === RequestType.AsbuiltsRequested;
  return (
    <StatusBadge
      tooltip={tooltip}
      color={color}
      aria-label={rowBadgeLabel(isAsbuilts ? 'As-Builts' : 'Map', trigger, rowName(a), tooltip.title)}
      data-testid={testId ?? (isAsbuilts ? 'asbuilt-request-cell' : 'map-request-cell')}
    >
      {trigger}
    </StatusBadge>
  );
};

const availableForDownloadTooltip = (a: UtilityAgency): StatusBadgeTooltip => ({
  title: 'Available For Download',
  description: (
    <div>
      {a.type} data is available for download. <StartDownload />
    </div>
  ),
});

const renderDownloadCell = (cell: Cell) => {
  const a = cell.row.original;
  if (isSubGeometryParentRow(a) && cell.row.getIsExpanded()) return null;
  const noAssetsPresent = hasNoAssetsPresent(a);

  // // 👇 these defaults should never be visible to the user
  let color: BadgeColor = 'grey';
  let tooltip: StatusBadgeTooltip = {
    title: 'Contact Support',
    description: 'There is an issue with this request, please contact Support.',
  };
  let trigger = 'Contact Support';
  let testId: string | undefined;

  if (a.isDigitized) {
    trigger = 'Yes - Digitized';
    color = 'green';
    tooltip = {
      title: 'Digitized Data Available for Download',
      description: 'Digitized assets for this project are available for download.',
    };
  } else if (noAssetsPresent) {
    trigger = 'N/A';
    color = 'green';
    tooltip = {
      title: 'Not Applicable',
      description: 'Based on the digital data in our platform, there are no assets present in the project boundary.',
    };
  } else if (a.isPublished) {
    if (a.applicableRestrictions && !isEmpty(a.applicableRestrictions)) {
      trigger = 'Approval Required';
      color = 'yellow';
      tooltip.title = 'Approval Required For Download';

      if (a.applicableRestrictions.urban === 0 || a.applicableRestrictions.rural === 0) {
        tooltip.description = (
          <div>
            {a.agencyName} requires manual approval from provider prior to download. <StartDownload />
          </div>
        );
      } else {
        tooltip.description = (
          <div>
            {a.agencyName} requires manual approval from provider for projects greater than{' '}
            {a.applicableRestrictions.urban !== undefined ? (
              <>{a.applicableRestrictions.urban} acres in urban areas</>
            ) : (
              <>{a.applicableRestrictions.rural} acres in rural areas</>
            )}
            . <StartDownload />
          </div>
        );
      }
    } else {
      trigger = 'Yes';
      color = 'green';
      tooltip = availableForDownloadTooltip(a);
    }
  } else if (isConnectbaseCovered(a, store.connectbaseCoverageEnabled)) {
    // Downloads never apply the 6 and 40 acre fiber limit, so a covered route gets no approval state.
    trigger = 'Yes';
    color = 'green';
    testId = 'download-connectbase-cell';
    tooltip = availableForDownloadTooltip(a);
  } else {
    trigger = '-';
    color = 'grey';
    tooltip = {
      title: 'Digital Data Not Available',
      description: <div>{a.type} data is NOT available for download at this time.</div>,
    };
  }

  return (
    <StatusBadge
      color={color}
      tooltip={tooltip}
      aria-label={rowBadgeLabel('Download', trigger, rowName(a), tooltip.title)}
      data-testid={testId ?? 'download-cell'}
    >
      {trigger}
    </StatusBadge>
  );
};

const renderDocumentsCell = (cell: Cell) => {
  const a = cell.row.original;
  if (isSubGeometryParentRow(a)) return null;

  return <DocumentsCell agency={a} />;
};

const renderAssetType = (cell: Cell) => {
  const a = cell.row.original;
  if (isSubGeometryParentRow(a) && cell.row.getIsExpanded()) return null;
  return a.type;
};

const renderLatestUpdate = (cell: Cell) => {
  const a = cell.row.original;
  if (isSubGeometryParentRow(a)) return null;

  return <LatestUpdateCell agency={a} />;
};

const renderHeaderWithTooltip = (title: string, tooltip: ReactNode) => {
  return (
    <div className="flex items-center space-x-1">
      <div>{title}</div>
      <InfoTooltip text={tooltip} label={`About ${title}`} />
    </div>
  );
};

// Custom sortable header: clicking cycles est time → confidence → off, instead of the default asc/desc
// toggle. Shared with the Select Assets step; only the test ids and PostHog `source` differ.
const renderEstTimeHeader = makeEstTimeSortHeader({ testIdPrefix: 'utilities', source: 'table' });

// The requests that belong to a row. A summary parent stands in for all of its Region children, so it
// reads every request for the agency; every other row matches only the requests in its own record.
const rowRequests = (a: UtilityAgency): EstTimeRequest[] =>
  Object.values(store.dataRequests).filter((r) =>
    isSubGeometryParentRow(a) ? r.agency.agencyId === a.agencyId : requestMatchesRow(r, a),
  );

const estTimeState = (a: UtilityAgency) => getEstTimeState(a, rowRequests(a));

// Kinds whose cell carries text rather than a bare dash. `requested`, `delivered` and `completed`
// qualify on the request alone, so they count even on the project-agencies-job-off payload, which
// omits responseStats.
const CELL_HAS_CONTENT: ReadonlySet<EstTimeState['kind']> = new Set([
  'estimate',
  'requested',
  'delivered',
  'completed',
]);

/**
 * Whether any agency row (or sub-row) has something other than a bare dash to show. Drives hiding the
 * whole column when every cell would be a dash (killswitch on, or nothing in flight and no usable stats).
 */
export const anyAgencyHasEstTime = (rows: readonly UtilityAgency[]): boolean =>
  rows.some((row) => {
    if (CELL_HAS_CONTENT.has(estTimeState(row).kind)) return true;
    return row.subRows ? anyAgencyHasEstTime(row.subRows) : false;
  });

const renderEstTime = (cell: Cell) => (
  <EstTimeCell state={estTimeState(cell.row.original)} testId="utilities-est-time-cell" />
);

const sortAgencyRows: SortingFn<UtilityAgency> = (a, b) => compareAgencyRows(a.original, b.original);

export const compactColumns: ColumnDef<UtilityAgency>[] = [
  {
    id: 'agencyName',
    header: 'Agency',
    size: 250,
    accessorKey: 'agencyName',
    cell: AgencyNameCell,
    sortingFn: sortAgencyRows,
  },
  {
    id: 'type',
    header: 'Asset',
    accessorKey: 'type',
    size: 95,
    cell: renderAssetType,
  },
  {
    id: 'availability',
    header: () => {
      return (
        <span className="inline-flex items-center space-x-1">
          <span>Availability/Status</span>
          <InfoTooltip
            label="About Availability/Status"
            text={
              <>
                For digital data (highlighted in green), shows confirmed presence or absence based on available data.
                For request data (highlighted in yellow or blue), shows status of records request. <br /> See this{' '}
                <a
                  target="_blank"
                  href="https://civilgrid.help.usepylon.com/articles/8372097607-request-status-utility-presence-and-outcome"
                  rel="noreferrer noopener"
                  className="text-blue-50 underline hover:text-blue-60"
                >
                  article
                </a>{' '}
                for detailed explanation.
              </>
            }
          />
        </span>
      );
    },
    size: AUTO_COL_WIDTH,
    cell: (cell) => {
      const a = cell.row.original;
      if (isSubGeometryParentRow(a)) return null;

      const noAssetsPresent = hasNoAssetsPresent(a);
      const mapRequest = getRequest(a.agencyId, RequestType.MapsRequested, a.agencySubGeometryId);
      const asBuiltsRequest = getRequest(a.agencyId, RequestType.AsbuiltsRequested, a.agencySubGeometryId);

      if (mapRequest || asBuiltsRequest) {
        const statuses = [mapRequest?.requestStatus, asBuiltsRequest?.requestStatus].filter((s) => !isUndefined(s));

        if (statuses.every((s) => s === RequestStatus.Complete)) {
          return <StatusBadge color="green">Complete</StatusBadge>; // all complete
        } else if (statuses.every((s) => s === RequestStatus.Cancelled || s === RequestStatus.Dismissed)) {
          // a mix of the two reads as cancelled, matching the per-request chip on the details page
          const trigger = statuses.some((s) => s === RequestStatus.Cancelled) ? 'Cancelled' : 'Dismissed';
          return <StatusBadge color="grey">{trigger}</StatusBadge>;
        }

        return <StatusBadge color="blue">View Request</StatusBadge>; // atleast 1 pending
      } else if (a.isDigitized) {
        return <StatusBadge color="green">Yes - Digitized</StatusBadge>;
      } else if (noAssetsPresent) {
        return <StatusBadge color="green">No Assets Present</StatusBadge>;
      } else if (a.availableDigitally) {
        return <StatusBadge color="green">Available Digitally</StatusBadge>;
      } else if (isConnectbaseCovered(a, store.connectbaseCoverageEnabled)) {
        return (
          <StatusBadge color="green" data-testid="availability-connectbase-cell">
            Available Digitally
          </StatusBadge>
        );
      } else {
        return <StatusBadge color="yellow">Request</StatusBadge>;
      }
    },
  },
];

class AttachmentsExportError extends Error {}

/** Extracted so the attachments-download failure toast can be exercised without rendering the column header. */
export const downloadAllAttachments = async (projectId: string, ccEmails?: string[]) => {
  try {
    const res = await apiDownloadCSAttachments(projectId, ccEmails);

    if (!res.success) {
      throw new AttachmentsExportError('Error downloading attachments');
    }

    notifyExportQueued({
      title: 'Downloading Attachments',
      message: 'Your attachments will be emailed to you',
      ccEmails,
    });
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectUtilitiesAttachmentsDownloaded, {
      project_id: projectId,
      cc_recipient_count: ccEmails?.length ?? 0,
    });
  } catch (err) {
    const reason = err instanceof AttachmentsExportError ? 'api_error' : getReasonForError(err);
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectUtilitiesAttachmentsDownloadFailed, {
      project_id: projectId,
      reason,
    });
    notifyDownloadError();
    return false;
  }
};

export const expandedColumns: ColumnDef<UtilityAgency>[] = [
  {
    id: 'agencyName',
    header: 'Agency',
    size: 250,
    accessorKey: 'agencyName',
    cell: AgencyNameCell,
    sortingFn: sortAgencyRows,
  },
  {
    id: 'type',
    header: 'Asset',
    accessorKey: 'type',
    size: 90,
    cell: renderAssetType,
  },
  {
    id: 'digitalAvailability',
    header: () =>
      renderHeaderWithTooltip(
        'Digital Availability',
        <>
          Digital data is GIS data that can be viewed on the CivilGrid map and downloaded to CAD or GIS. This data is
          direct-sourced from agencies/owners and is routinely updated per the <i>Latest Update</i> column.
        </>,
      ),
    size: 75,
    columns: [
      {
        id: 'availableDigitally',
        size: 110,
        header: () =>
          renderHeaderWithTooltip(
            'Live Map',
            'Digital map data availability and/or presence. Hover over the text for a more detailed explanation.',
          ),
        accessorKey: 'isPublished',
        cell: renderAvailabilityDigital,
      },
      {
        id: 'download',
        header: () =>
          renderHeaderWithTooltip(
            'Download',
            'Digital map data is typically available for download to CAD or GIS. Area limits or approvals may be required for certain asset types. Hover over the text for details.',
          ),
        cell: renderDownloadCell,
        size: 150,
      },
    ],
  },
  {
    id: 'recordsResearchStatus',
    header: () =>
      renderHeaderWithTooltip(
        'Records Research Status',
        'Request Maps and as-builts through our premium CivilSearch service. CivilGrid will handle utility requests on your behalf, managing the entire process from start to finish.',
      ),
    columns: [
      {
        id: RequestType.MapsRequested,
        header: () =>
          renderHeaderWithTooltip(
            'Map',
            'Depicts Request Status, Utility Presence, and/or Request Outcome of ongoing records research requests for map PDFs. Initiate a new request by clicking the orange Request Data button to the top-right.',
          ),
        cell: renderAvailabilityRecordsResearch,
        size: 150,
      },
      {
        id: RequestType.AsbuiltsRequested,
        header: () =>
          renderHeaderWithTooltip(
            'As-Builts',
            'Depicts Request Status, Utility Presence, and/or Request Outcome of ongoing records research requests for as-builts. Initiate a new request by clicking the orange Request Data button to the top-right.',
          ),
        cell: renderAvailabilityRecordsResearch,
        size: 150,
      },
    ],
  },
  {
    id: 'attachments',
    header: ({ table }) => {
      let hasFiles = false;
      const { dataRequests } = useSnapshot(store) as typeof store;

      for (let i = 0; i < table.getRowCount(); i++) {
        const r = table.getRowModel().rows[i];
        const requests = Object.values(dataRequests).filter((d) => d.agency.agencyId === r.original.agencyId);
        const filesAvailable = requests.some((r) => r.files.length > 0);
        if (filesAvailable) {
          hasFiles = true;
          break;
        }
      }

      const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;

      const [downloadAllOpen, setDownloadAllOpen] = useState(false);

      return (
        <div className="flex flex-row space-x-1">
          {renderHeaderWithTooltip('Files', 'Files received by CivilGrid from agencies.')}
          {hasFiles && (
            <Tooltip asChild text="Download all files">
              <IconButton
                variant="hollow"
                icon="download"
                aria-label="Download all files"
                data-testid="civilsearch-attachments-download-all"
                onClick={() => setDownloadAllOpen(true)}
              />
            </Tooltip>
          )}
          <SendEmailModal
            open={downloadAllOpen}
            onOpenChange={setDownloadAllOpen}
            title="Download all files"
            testIdPrefix="civilsearch-attachments"
            onSend={(ccEmails) => downloadAllAttachments(projectId, ccEmails.length > 0 ? ccEmails : undefined)}
          />
        </div>
      );
    },
    cell: renderDocumentsCell,
    size: 125,
  },
  {
    id: 'utilityNotes',
    size: 200,
    header: () =>
      renderHeaderWithTooltip(
        'Notes',
        <div>
          <div>Use this section to store quick notes.</div>
          <br />
          <div>
            Note: This is a shared space, i.e. users in your organization with access to this project can add/delete
            contents.
          </div>
        </div>,
      ),
    cell: (cell) => {
      const { projectId } = useParams({ strict: false }) as { projectId: string };
      const agency = cell.row.original;
      if (isSubGeometryParentRow(agency)) return null;
      // CG-1219: a request-only row has no projectUtilityNotes record, so there is no id to
      // commit an edit against.
      if (agency.isRequestOnly) return <div data-testid="utilities-notes-request-only">—</div>;

      async function onCommit(notes: string) {
        const res = await apiUpdateUtilityNote(projectId, agency.utilityNotes.id, notes);

        if (!res.id) throw new Error('Bad response!');
        const note = store.agencies[agency.id].utilityNotes;

        store.agencies[agency.id].utilityNotes = {
          ...note,
          ...res,
        };
      }

      return <FreeFormTextCell initialValue={cell.row.original.utilityNotes?.notes ?? ''} onCommit={onCommit} />;
    },
  },
  {
    id: 'estTime',
    // Display-only: sorting is driven by the hidden estTimeDays / estTimeConfidence columns via the
    // custom header, so TableV2 does not add its own asc/desc arrow or click handler on this column.
    enableSorting: false,
    size: 110,
    header: renderEstTimeHeader,
    cell: renderEstTime,
  },
  {
    // Hidden helper column backing the "sort by est time" mode. Raw days (numeric); undefined rows last.
    id: 'estTimeDays',
    accessorFn: (a) => surfacedEstimateDays(estTimeState(a)) ?? undefined,
    sortUndefined: 'last',
    enableHiding: true,
    // Never rendered → zero layout width. TableV2's auto-resize subtracts every column's size (hidden
    // included) from the available width; a default-sized hidden column steals ~150px.
    size: 0,
    minSize: 0,
  },
  {
    // Hidden helper column backing the "sort by confidence" mode. Never rendered (visibility forced off);
    // ranks only rows that surface a range, others sort last.
    id: 'estTimeConfidence',
    accessorFn: (a) => (surfacedEstimateDays(estTimeState(a)) === null ? undefined : getResponseConfidenceRank(a)),
    sortUndefined: 'last',
    enableHiding: true,
    // Zero-width for the same reason as estTimeDays above.
    size: 0,
    minSize: 0,
  },
  {
    id: 'latestUpdates',
    header: () =>
      renderHeaderWithTooltip(
        'Latest Update',
        <div>
          Latest activity and updates for data source dates and records research requests.
          <br />
          [D] = Digital data - refresh dates and cadences
          <br />
          [M] = Map PDF requests
          <br />
          [AB] = As-Built requests
        </div>,
      ),
    cell: renderLatestUpdate,
    size: AUTO_COL_WIDTH,
  },
];
